import React, { useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useMarketplaceGroups from "@shared/features/newMarketplace/hooks/useMarketplaceGroups";
import Card from "common/Boxes/Card";
import GroupsTable from "features/newMarketplace/GroupsTable";
import useSearchInput from "utils/hooks/useSearchInput";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useMedia from "utils/hooks/useMedia";
import { LanguageNameSpaces, MarketplaceSubGroup } from "@shared/types";
import GroupsCards from "features/newMarketplace/GroupsCards";
import GroupsWrapper from "features/newMarketplace/GroupsWrapper";
import { useBottomNavigator } from "utils/hooks/misc";
import FlatList from "common/Lists/FlatList";
import GroupCard from "features/newMarketplace/GroupCard";
import EmptyView from "common/Empty/EmptyView";
import { marketplaceFilters } from "@shared/slices/filters.slice";
import { MOBILE_SEARCH, TABLET } from "common/constants";
import { EmptyStatesType } from "common/Icons/types.ts";
import styled, { useTheme } from "styled-components";
import GroupsListMessage from "features/newMarketplace/NewLook/GroupsListMessage.tsx";
import PageTabs from "common/Tabs/PageTabs.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface GroupsProps {
  openScoreModal?: (group: MarketplaceSubGroup) => void;
  showFullList?: boolean;
  setShowFullList?: (showFullList: boolean) => void;
}

const Groups = ({
  openScoreModal,
  showFullList,
  setShowFullList,
}: GroupsProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { useMobileSearch, search: mobileSearch } =
    useContext(PageWrapperContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallScreen = useMedia([`(max-width: 1300px)`], [true], false);
  const [isCardsView, setIsCardsView] = useState(true);
  const { isNewLook } = useTheme();
  const [SearchInput, searchInputValue] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: isNewLook
      ? { width: 220, height: 32, borderRadius: 8 }
      : { width: "200px", borderRadius: 8 },
    lowerCaseValue: true,
  });
  const searchValue = useMobileSearch
    ? mobileSearch.value
    : (searchInputValue as string);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const dispatch = useDispatch();

  const {
    getMarketplaceInfoQuery,
    sortedGroups,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    showFullListToggle,
  } = useMarketplaceGroups({
    searchValue,
    showFullList,
  });

  const numActiveFilters = useSelector(
    marketplaceFilters.selectors.selectNumActiveFilters
  );
  const hasData = !!getMarketplaceInfoQuery.data?.data.length;
  const newLookEmptyState = useMemo(() => {
    const objectName = t("empty.object_name");
    if (hasData && searchValue?.length) {
      return (
        <GroupsListMessage
          title={tCommon("empty.no_results_matching_search", { objectName })}
        />
      );
    }
    if (hasData && numActiveFilters) {
      return (
        <GroupsListMessage
          title={tCommon("empty.no_results_matching_filters", { objectName })}
          actionLabel={tCommon("filters.common.reset")}
          onAction={() =>
            dispatch(marketplaceFilters.actions.selectAll(undefined))
          }
        />
      );
    }
    return <GroupsListMessage title={t("empty.title")} />;
  }, [hasData, searchValue, numActiveFilters, dispatch, t, tCommon]);

  const emptyState = useMemo(
    () => (
      <EmptyView
        iconKey={EmptyStatesType.Marketplace}
        iconWidth={isMobile ? 100 : 140}
        title={t("empty.title")}
        filtersSlice={marketplaceFilters}
        objectName={t("empty.object_name")}
        hasData={!!getMarketplaceInfoQuery.data?.data.length}
        isSearch={!!searchValue?.length}
        filtersChipsOpen={TABLET}
      />
    ),
    [searchValue, getMarketplaceInfoQuery.data, isMobile, i18n.language]
  );

  const isNewLookDesktop = isNewLook && !isBottomNavigator;
  const commonProps = {
    groups: sortedGroups,
    loading: getMarketplaceInfoQuery.isLoading,
    openScoreModal,
    emptyState: isNewLookDesktop ? newLookEmptyState : emptyState,
  };
  const sortProps = {
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
  };

  const table = <GroupsTable {...commonProps} {...sortProps} />;
  const cards = <GroupsCards {...commonProps} />;

  const wrapper = (
    <GroupsWrapper
      {...sortProps}
      isCardsView={isCardsView}
      setIsCardsView={setIsCardsView}
      SearchInput={SearchInput}
      useMobileSearch={useMobileSearch}
      showFullList={showFullList}
      setShowFullList={setShowFullList}
      showFullListToggle={showFullListToggle}
      numGroups={sortedGroups.length}
    />
  );

  const renderGroupCard = ({
    item,
    index,
  }: {
    item: MarketplaceSubGroup;
    index: number;
  }) => {
    return (
      <GroupCard
        key={item?.group_id}
        group={item}
        index={index}
        openScoreModal={openScoreModal}
      />
    );
  };

  const tabsData = useMemo(() => {
    return {
      [MarketplaceTab.ALL]: sortedGroups,
      [MarketplaceTab.NEW_ARRIVALS]: sortedGroups.filter(
        (group) => group.is_new
      ),
      [MarketplaceTab.FREE_TRIAL]: sortedGroups.filter(
        (group) => group.has_free_trial
      ),
    };
  }, [sortedGroups]);

  const getCardsList = useCallback(
    (marketplaceTab: MarketplaceTab) => {
      const groupsData = tabsData[marketplaceTab];
      return (
        <FlatList
          data={groupsData}
          renderContent={renderGroupCard}
          loading={getMarketplaceInfoQuery.isLoading}
          refreshing={getMarketplaceInfoQuery.isFetching}
          onRefresh={() => {
            try {
              getMarketplaceInfoQuery.refetch();
            } catch {}
          }}
          emptyState={emptyState}
          listStyle={{ paddingBottom: 16 }}
        />
      );
    },
    [
      tabsData,
      getMarketplaceInfoQuery.isLoading,
      getMarketplaceInfoQuery.isFetching,
      emptyState,
    ]
  );

  const tabsItems = useMemo(
    () =>
      Object.values(MarketplaceTab).map((tab) => {
        return {
          key: tab,
          label: t(tab),
          children: getCardsList(tab),
          forceRender: true,
        };
      }),
    [getCardsList, i18n.language]
  );

  if (isBottomNavigator) {
    return (
      <MobileWrapper>
        <PageTabs items={tabsItems} commonContent={wrapper} />
      </MobileWrapper>
    );
  }

  if (isNewLook) {
    const showCards = isSmallScreen && isCardsView;
    const failed = getMarketplaceInfoQuery.isError && !hasData;
    const list = showCards ? cards : table;
    return (
      <GroupsWrapper
        {...sortProps}
        isCardsView={isCardsView}
        setIsCardsView={setIsCardsView}
        SearchInput={SearchInput}
        useMobileSearch={useMobileSearch}
        showFullList={showFullList}
        setShowFullList={setShowFullList}
        showFullListToggle={showFullListToggle}
        numGroups={
          getMarketplaceInfoQuery.isLoading || failed
            ? undefined
            : sortedGroups.length
        }
        asPanel={!showCards || failed}
        hideFilters={failed}
      >
        {failed ? (
          <GroupsListMessage
            title={t("errors.groups_title")}
            description={t("errors.description")}
            actionLabel={t("errors.retry")}
            onAction={getMarketplaceInfoQuery.refetch}
          />
        ) : (
          list
        )}
      </GroupsWrapper>
    );
  }

  if (isSmallScreen) {
    return (
      <>
        {wrapper}
        {isCardsView ? cards : <Card>{table}</Card>}
      </>
    );
  }

  return (
    <Card>
      {wrapper}
      {table}
    </Card>
  );
};

export default Groups;

enum MarketplaceTab {
  ALL = "tabs.all",
  NEW_ARRIVALS = "tabs.new_arrivals",
  FREE_TRIAL = "tabs.free_trial",
}

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
`;
